import {gsap, TimelineLite} from "gsap";
import {HOTB} from "../../HOTB";
import {ISymbol} from "../../../core/reel/IReelData";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/presentation/SimplePresentationRunner";
import {GameUtils} from "../../utils/GameUtils";
import {
    GameSymbol,
    LowSymbolsList,
    MidSymbolsList,
    SpecialSymbolsList,
    SymbolsList,
    SymbolStates
} from "../../reels/GameSymbol";
import {GameSounds} from "../../sound/GameSounds";
import {HOTBGameData, xBombItem} from "../../server/HOTBData";
import {GameConfig} from "../../GameConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {AvalancheReelModel} from "../../reels/AvalancheReelModel";

export class WinExplodePresentation implements SimplePresentation {
    private _mainTl: TimelineLite;

    public shouldRun(): boolean {
        const gameData: HOTBGameData = HOTB.model.gameData;

        let flag: boolean = false;

        if (gameData.betWayWins.length > 0 || gameData.xBombList.length > 0) {
            flag = true;
        }

        return flag;
    }

    public start(controller: SimplePresentationRunnerController): void {
        controller.setSkipCallback((): void => {
            this.onSkip();
        });

        this._mainTl = new TimelineLite({
            delay: 0.2,
            onComplete: (): void => {
                this._mainTl.kill();
                controller.finish();
            }
        });

        const gameData: HOTBGameData = HOTB.model.gameData,
            winningTL: TimelineLite = new TimelineLite(),
            winPositions: boolean[][] = HOTB.model.allExplodeWinPositions,
            explodeTL: TimelineLite = new TimelineLite(),
            xBombExplodePositions: boolean[][] = this.markAdjacentWithTrue(gameData.evaluatedArea);

        winningTL.add((): void => {
            HOTB.reelArea.mainReelModels.forEach((reel: AvalancheReelModel): void => {
                reel.reelView.blinkSoundPlayed = false;
            });
        });

        // win symbols
        for (let reelId: number = 0; reelId < winPositions.length; reelId++) {
            for (let symId: number = 0; symId < winPositions[reelId].length; symId++) {
                let symbol: ISymbol = HOTB.reelArea.getSymbolAtPosition(reelId, symId);

                if (symbol && winPositions[reelId][symId]) {
                    winningTL.add((<GameSymbol>symbol).getStateAnimation(SymbolStates.WIN), 0);

                    if (!(gameData.brokeTheBank) && ((<GameSymbol>symbol).symbolName !== SymbolsList.XBOMB)) {
                        winningTL.add((): void => {
                            symbol.isRemoved = true;
                            HOTB.reelArea.mainReelModels[reelId].reelView.removeSymbol(symbol);
                        }, ">");     //#257  to remove each symbol simultaneously in timeline after win animation
                    }
                }
            }
        }

        this._mainTl.add(HOTB.eachSymWinView.showWinAndWays(0.5));
        this._mainTl.add([
            winningTL,
            (): void => {
                HOTB.soundPlayer.playSound(GameSounds.SYMS_EXPLODE);
            }
        ]);

        // xBomb explode symbols
        if (!(gameData.brokeTheBank)) {
            for (let reelId: number = 0; reelId < xBombExplodePositions.length; reelId++) {
                for (let symId: number = 0; symId < xBombExplodePositions[reelId].length; symId++) {
                    let symbol: ISymbol = HOTB.reelArea.getSymbolAtPosition(reelId, symId);

                    if (xBombExplodePositions[reelId][symId]) {
                        explodeTL.add([
                            (<GameSymbol>symbol).getStateAnimation(SymbolStates.BLAST),
                            (): void => {
                                HOTB.soundPlayer.playSideReelFeatureSounds(GameSounds.XBOMB_EXPLODE)
                            }
                        ], 0);
                        explodeTL.add((): void => {
                            symbol.isRemoved = true;
                            HOTB.reelArea.mainReelModels[reelId].reelView.removeSymbol(symbol);
                        });
                    }
                }
            }
        }

        if (gameData.xBombList.length > 0) {
            this._mainTl.add(explodeTL, "-=0.2");

            this._mainTl.add([
                HOTB.gameMultiplier.showGlow(),
                (): void => {
                    HOTB.gameMultiplier.updateValue(gameData.gameMultiplierAfter);
                    HOTB.soundPlayer.playSound(GameSounds.MAIN_GAME_MULTI_BOARD_UPDATE);
                }
            ], "+=0.1");
        }


        if (!gameData.brokeTheBank) {
            this._mainTl.add([
                gsap.to(this, {
                    duration: HOTB.reelArea.getAllReelCollapseDuration()
                }),
                (): void => {
                    HOTB.reelArea.collapse();
                    if (!(gameData.brokeTheBank)) {
                        HOTB.keypadPlugin.setWin(gameData.accumulatedRoundWin, true, GameUtils.isWinBelowStake());
                    }
                    controller.setSkipCallback();
                }
            ]);
        }

        if (gameData.brokeTheBank) {
            this._mainTl.add((): void => {
                for (let i: number = 0; i < gameData.xBombList.length; i++) {
                    const currBomb: xBombItem = gameData.xBombList[i],
                        currBombReelId: number = currBomb.toCoord.x,
                        currBombSymId: number = currBomb.toCoord.y;

                    HOTB.reelArea.mainReelModels[currBombReelId].reelView.switchXBombForMaxWin(currBombReelId, currBombSymId);
                }
            });
        }
    }

    private onSkip(): void {
        this._mainTl.timeScale(4);
        // this._mainTl.progress(1);
    }

    private markAdjacentWithTrue(myArr: string[][]): boolean[][] {
        const rows: number = myArr.length,
            cols: number = myArr[0].length,
            result: boolean[][] = ArrayHelper.initArrayWithValues(GameConfig.totalReels, () => {
                return ArrayHelper.initArrayWithValues(GameConfig.totalSymsPerReel, (): boolean => false);
            });

        for (let reelId: number = 0; reelId < rows; reelId++) {
            for (let symId: number = 0; symId < cols; symId++) {
                if (myArr[reelId][symId] === SpecialSymbolsList.XBOMB) {
                    for (let x: number = -1; x <= 1; x++) {
                        for (let y: number = -1; y <= 1; y++) {
                            const newRow: number = reelId + x;
                            const newCol: number = symId + y;

                            if (newRow >= 0 && newRow < rows && // Check row bounds
                                newCol >= 0 && newCol < cols // Check column bounds
                            ) {
                                const adjacentSymbol: string = myArr[newRow][newCol];

                                // Mark true if it's in LowSymbolsList, MidSymbolsList, or is XBOMB itself
                                if (
                                    adjacentSymbol === SpecialSymbolsList.XBOMB ||
                                    Object.values(LowSymbolsList).includes(adjacentSymbol as LowSymbolsList) ||
                                    Object.values(MidSymbolsList).includes(adjacentSymbol as MidSymbolsList)
                                ) {
                                    result[newRow][newCol] = true;
                                }
                            }
                        }
                    }
                }
            }
        }

        return result;
    }
}